import $ from 'cash-dom';

const GROUPING_SIZE = 3;
const DEFAULT_MIN_GROUPING_DIGITS = 1;

let instance = null;

class NumberFormatter {
	constructor() {
		if (!instance) {
			this._currencyOptions = this._getCurrencyOptions();

			const $body = $('body');
			const bodyLanguage = $body.data('language');
			const locale = bodyLanguage ? bodyLanguage : 'en';

			const bodyCurrency = $body.data('currency');
			const currency = bodyCurrency ? bodyCurrency : 'USD';

			const bodyThousandsSeparator = $body.attr('data-thousands-separator');
			const bodyDecimalSeparator = $body.attr('data-decimal-separator');
			const bodyMinGroupingDigits = parseInt($body.attr('data-min-grouping-digits'), 10);

			if (bodyThousandsSeparator !== undefined && bodyThousandsSeparator !== null && bodyThousandsSeparator !== '') {
				// Hand-rolled rather than `new Intl.NumberFormat(locale, {useGrouping: 'auto'})`: the
				// separators and grouping threshold come from the server's NumberHelper table via these
				// body attributes, so JS output stays pinned to the same CLDR data PHP used to render the
				// page, regardless of what CLDR version the visitor's own browser ships.
				const enFormatter = new Intl.NumberFormat('en');
				const decimalSep = bodyDecimalSeparator || (bodyThousandsSeparator === ',' ? '.' : ',');
				// CLDR groupingSize (3) + minimumGroupingDigits: Spanish and friends only show a
				// separator from five integer digits on, so "3000" stays ungrouped but "12.345" doesn't.
				const groupFrom = GROUPING_SIZE + (isNaN(bodyMinGroupingDigits) ? DEFAULT_MIN_GROUPING_DIGITS : bodyMinGroupingDigits);
				this._numberFormatter = {
					format: (value) => {
						const parts = enFormatter.formatToParts(value);
						const integerDigits = parts.reduce(
							(total, p) => p.type === 'integer' ? total + p.value.length : total,
							0
						);
						const grouped = integerDigits >= groupFrom;

						return parts.map(p => {
							if (p.type === 'group') {
								return grouped ? bodyThousandsSeparator : '';
							}
							if (p.type === 'decimal') {
								return decimalSep;
							}
							return p.value;
						}).join('');
					}
				};
			} else {
				this._numberFormatter = new Intl.NumberFormat(locale);
			}

			const currencyOptions = {
				style: 'currency',
				currency: currency,
			};

			this._currencyFormatter = new Intl.NumberFormat(locale, currencyOptions);

			this._currencyFormatterNoFraction = new Intl.NumberFormat(
				locale,
				{...currencyOptions, maximumFractionDigits: 0}
			);

			this._thousandSeparator = this._numberFormatter.format(11111).replace(/\p{Number}/gu, '');
			this._decimalSeparator = this._numberFormatter.format(1.1).replace(/\p{Number}/gu, '');

			instance = this;
		}

		return instance;
	}

	/**
	 * @return {NumberFormatter}
	 */
	static getInstance() {
		if (!NumberFormatter._instance) {
			NumberFormatter._instance = new NumberFormatter();
		}

		return NumberFormatter._instance;
	}

	_getCurrencyOptions() {
		const stringData = $('#currency-data').val();
		let options = {
			currency: 'USD',
			symbol: '$',
			template: '${{amount}}',
			format: true,
			thousands_separator: null,
		};

		if (stringData) {
			options = JSON.parse(stringData);
		}

		return options;
	}

	get currencyOptions() {
		return this._currencyOptions;
	}

	get numberFormatter() {
		return this._numberFormatter;
	}

	get currencyFormatter() {
		return this._currencyFormatter;
	}

	get currencyFormatterNoFraction() {
		return this._currencyFormatterNoFraction;
	}

	parseFormattedNumber(stringNumber) {
		return parseFloat(stringNumber
			.replace(new RegExp('\\' + this._thousandSeparator, 'g'), '')
			.replace(new RegExp('\\' + this._decimalSeparator), '.')
		);
	}
}

export default NumberFormatter;
